import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Space,
  message,
  Divider,
  Alert,
  Typography,
  Spin,
} from 'antd';
import { SaveOutlined, ReloadOutlined, LockOutlined } from '@ant-design/icons';
import apiService from '../../../services/apiService';

const { Title, Text, Paragraph } = Typography;

interface SFConfig {
  id?: string;
  clientCode: string;
  checkwordSandbox?: string;
  checkwordProd?: string;
  custId?: string;
  templateCode?: string;
  isProduction: boolean;
  isActive: boolean;
  hasCheckwordSandbox?: boolean;
  hasCheckwordProd?: boolean;
  accountType?: string;
}

/**
 * SF Express B2B Configuration Settings Page
 * Allows administrators to configure SF Express B2B API credentials
 */
const SFExpressB2BSettings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [config, setConfig] = useState<SFConfig | null>(null);

  /**
   * Load existing configuration on mount
   */
  useEffect(() => {
    loadConfig();
  }, []);

  /**
   * Load SF Express B2B configuration
   */
  const loadConfig = async () => {
    setFetching(true);
    try {
      const response: any = await apiService.get('/sf-express/config?accountType=b2b');
      console.log('[SFExpressB2BSettings] loadConfig response:', response);

      // apiService.get returns parsed JSON directly: { success: true, data: {...} }
      if (response && response.success && response.data) {
        const configData = response.data;
        setConfig(configData);

        // Populate form with existing data
        form.setFieldsValue({
          clientCode: configData.clientCode,
          custId: configData.custId,
          templateCode: configData.templateCode,
          isProduction: configData.isProduction,
          isActive: configData.isActive,
        });
      } else {
        // No configuration exists yet - this is normal for first-time setup
        console.log('No SF Express B2B configuration found - ready for initial setup');
        setConfig(null);
      }
    } catch (error: any) {
      console.error('Error loading SF Express B2B config:', error);
      // Only show error if it's not a "no config" scenario
      if (error.message && !error.message.includes('404')) {
        message.error('Failed to load configuration');
      }
    } finally {
      setFetching(false);
    }
  };

  /**
   * Save SF Express B2B configuration
   */
  const handleSave = async (values: any) => {
    console.log('[SFExpressB2BSettings] handleSave called with values:', values);
    setLoading(true);
    try {
      console.log('[SFExpressB2BSettings] Calling apiService.post...');
      const response: any = await apiService.post('/sf-express/config', {
        ...values,
        accountType: 'b2b',
      });
      console.log('[SFExpressB2BSettings] API response:', response);

      // apiService.post returns parsed JSON directly: { success: true, data: {...} }
      // Check the response object's success property directly
      if (response && response.success) {
        message.success('SF Express B2B configuration saved successfully');
        setConfig(response.data);
        loadConfig(); // Reload to get masked passwords
      } else {
        console.error('[SFExpressB2BSettings] Response indicates failure:', response);
        message.error(response?.error || 'Failed to save configuration');
      }
    } catch (error: any) {
      console.error('[SFExpressB2BSettings] Error saving SF Express B2B config:', error);
      message.error(error.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Loading SF Express B2B settings..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <Card>
        <Title level={3}>SF Express B2B Integration Settings</Title>
        <Paragraph type="secondary">
          Configure SF Express B2B API credentials for business-to-business shipments.
          This is a separate account from the standard SF Express integration.
        </Paragraph>

        <Alert
          message="B2B Account"
          description="This configuration is for SF Express B2B (Business-to-Business) shipments. If you need to configure standard SF Express for consumer shipments, please use the regular SF Express Settings page."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            isProduction: false,
            isActive: true,
          }}
        >
          <Divider orientation="left">API Credentials</Divider>

          <Form.Item
            name="clientCode"
            label="Client Code (B2B)"
            rules={[{ required: true, message: 'Please enter client code' }]}
            extra="Your SF Express B2B API client code (different from standard account)"
          >
            <Input placeholder="Enter B2B client code" size="large" />
          </Form.Item>

          <Form.Item
            name="custId"
            label="Monthly Account Number"
            extra="Optional: Monthly settlement customer ID for B2B billing"
          >
            <Input placeholder="Enter monthly account number (optional)" size="large" />
          </Form.Item>

          <Form.Item
            name="templateCode"
            label="Waybill Template Code (B2B)"
            extra={
              <span>
                Template code for B2B waybill PDF generation. Get this from{' '}
                <a href="https://open.sf-express.com/billPrint/305090" target="_blank" rel="noopener noreferrer">
                  SF Express Print Settings
                </a>
                . Format: fm_150_standard_XXXXXX
              </span>
            }
          >
            <Input placeholder="e.g., fm_150_standard_XXXXXX" size="large" />
          </Form.Item>

          <Divider orientation="left">API Keys (Checkwords)</Divider>

          <Form.Item
            name="checkwordSandbox"
            label={
              <Space>
                <LockOutlined />
                <Text>Sandbox Checkword (B2B)</Text>
              </Space>
            }
            extra={config?.hasCheckwordSandbox
              ? "Leave empty to keep existing checkword, or enter a new one to update"
              : "Optional: API checkword for sandbox/testing environment"}
          >
            <Input.Password
              placeholder={config?.hasCheckwordSandbox ? "Leave empty to keep existing" : "Enter sandbox checkword (optional)"}
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="checkwordProd"
            label={
              <Space>
                <LockOutlined />
                <Text>Production Checkword (B2B)</Text>
              </Space>
            }
            rules={[{ required: !config, message: 'Please enter production checkword' }]}
            extra={config?.hasCheckwordProd
              ? "Leave empty to keep existing checkword, or enter a new one to update"
              : "Required: API checkword for production environment"}
          >
            <Input.Password
              placeholder={config?.hasCheckwordProd ? "Leave empty to keep existing" : "Enter production checkword"}
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Divider orientation="left">Settings</Divider>

          <Form.Item
            name="isProduction"
            label="Production Mode"
            valuePropName="checked"
            extra="Enable production mode to use real SF Express API. Disable for testing with sandbox."
          >
            <Switch
              checkedChildren="Production"
              unCheckedChildren="Sandbox"
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Enable SF Express B2B Integration"
            valuePropName="checked"
            extra="Enable or disable SF Express B2B integration for this company"
          >
            <Switch
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
            />
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Save Configuration
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadConfig}
                disabled={loading}
                size="large"
              >
                Reload
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {config && (
          <Alert
            message="B2B Configuration Status"
            description={
              <div>
                <Text>Client Code: {config.clientCode}</Text>
                <br />
                <Text>Monthly Account: {config.custId || 'Not set'}</Text>
                <br />
                <Text>Template Code: {config.templateCode || 'Not set'}</Text>
                <br />
                <Text>Environment: {config.isProduction ? 'Production' : 'Sandbox'}</Text>
                <br />
                <Text>Status: {config.isActive ? 'Active' : 'Inactive'}</Text>
              </div>
            }
            type="success"
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
      </Card>
    </div>
  );
};

export default SFExpressB2BSettings;